/**
 * Cookie utilities for client-side cookie management
 */

export interface CookieOptions {
	path?: string;
	domain?: string;
	maxAge?: number;
	expires?: Date;
	secure?: boolean;
	sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * Get a cookie value by name
 * @param name Cookie name
 * @returns Cookie value or undefined if not found
 */
export function getCookie(name: string): string | undefined {
	if (typeof document === 'undefined') return undefined;

	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) {
		return parts.pop()?.split(';').shift();
	}
	return undefined;
}

/**
 * Set a cookie
 * @param name Cookie name
 * @param value Cookie value
 * @param options Cookie options
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
	if (typeof document === 'undefined') return;

	const {
		path = '/',
		domain,
		maxAge,
		expires,
		secure = false,
		sameSite = 'Lax'
	} = options;

	let cookieString = `${name}=${encodeURIComponent(value)}`;

	if (path) cookieString += `; path=${path}`;
	if (domain) cookieString += `; domain=${domain}`;
	if (maxAge !== undefined) cookieString += `; max-age=${maxAge}`;
	if (expires) cookieString += `; expires=${expires.toUTCString()}`;
	if (secure) cookieString += '; secure';
	if (sameSite) cookieString += `; samesite=${sameSite}`;

	document.cookie = cookieString;
}

/**
 * Delete a cookie
 * @param name Cookie name
 * @param options Cookie options (path and domain should match the original cookie)
 */
export function deleteCookie(name: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}): void {
	setCookie(name, '', { ...options, maxAge: -1 });
}
